import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Upload, Loader2, MapPin, Hash, Landmark, AlertTriangle, Plus, Pencil, Trash2,
  RefreshCw, Fingerprint, Copy,
} from "lucide-react";
import { SepaStatusBadge } from "@/components/SepaStatusBadge";
import { CatastoHeaderInfo } from "@/components/CatastoPanel";
import { EditBuildingButton } from "@/components/condomini/BuildingForm";

export const AnagraficaHeader = ({
  selected, buildingImporting, buildingImportRef, importIntoBuilding, catastoSync,
  totalMillesimi, residentsCount, missingCf, sortKey, setSortKey, setSortDir,
  onEditSaved, onAddResident, onClearAnagrafica, onOpenImport,
}) => (
  <div className="px-4 sm:px-6 py-4 border-b border-border flex flex-col sm:flex-row sm:items-start sm:justify-between flex-wrap gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-head text-lg font-semibold">Anagrafica — {selected.name}</h3>
        <SepaStatusBadge status={selected.sepa_status} />
        {selected.code && (
          <button type="button" data-testid="building-code-chip"
            title="Codice univoco del condominio — clicca per copiare"
            onClick={() => { navigator.clipboard?.writeText(selected.code)?.catch(() => {}); toast.success(`Codice ${selected.code} copiato`); }}
            className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold rounded-full px-2.5 py-1 bg-[#007BFF]/10 text-[#007BFF] border border-[#007BFF]/20 hover:bg-[#007BFF]/20 transition-colors">
            <Fingerprint className="h-3.5 w-3.5" />{selected.code}
            <Copy className="h-3 w-3 opacity-60" />
          </button>
        )}
        <EditBuildingButton building={selected} onSaved={onEditSaved} />
        <input ref={buildingImportRef} type="file" className="hidden" accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv" onChange={importIntoBuilding} data-testid="building-import-input" />
        <Button data-testid="building-import-btn" size="sm" variant="outline"
          disabled={buildingImporting || selected.subscription_status !== "attivo"}
          title={selected.subscription_status !== "attivo" ? "Condominio non collegato: attivalo per usare questa funzione" : ""}
          onClick={onOpenImport}
          className="rounded-md h-8 border-[#007BFF]/40 text-[#007BFF] hover:bg-[#007BFF]/5 disabled:opacity-40 disabled:cursor-not-allowed">
          {buildingImporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
          Importa condòmini con AI
        </Button>
        <Button data-testid="catasto-sync-btn" size="sm" variant="outline"
          disabled={catastoSync.busy || catastoSync.gated}
          title={catastoSync.gated ? "Condominio non collegato: attivalo per scaricare i dati catastali" : "Scarica gli immobili dal Catasto partendo dai codici fiscali dei condòmini"}
          onClick={() => catastoSync.sync()}
          className="rounded-md h-8 border-[#007BFF]/40 text-[#007BFF] hover:bg-[#007BFF]/5 disabled:opacity-40 disabled:cursor-not-allowed">
          {catastoSync.busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
          {selected.catasto ? "Aggiorna catasto" : "Scarica dati catastali"}
        </Button>
      </div>
      {selected.address && (
        <span data-testid="building-address" className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
          <MapPin className="h-3 w-3" />{selected.address}
          {(selected.cap || selected.citta) ? ` · ${selected.cap || ""} ${selected.citta || ""}`.replace(/\s+/g, " ").trimEnd() : ""}
          {selected.provincia ? ` (${selected.provincia})` : ""}
        </span>
      )}
      {selected.codice_fiscale && <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Hash className="h-3 w-3" />C.F. {selected.codice_fiscale}</span>}
      {selected.iban && <span data-testid="building-iban" className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 font-mono"><Landmark className="h-3 w-3" />IBAN {selected.iban}</span>}
      {selected.sdi && <span data-testid="building-sdi" className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 font-mono"><Hash className="h-3 w-3" />SDI {selected.sdi}</span>}
      <CatastoHeaderInfo catasto={selected.catasto} className="mt-0.5" />
    </div>
    <div className="flex items-center gap-3 flex-wrap">
      <div className="text-sm text-muted-foreground">Tot. millesimi: <span className="font-semibold text-foreground">{totalMillesimi.toFixed(0)}</span> · {residentsCount} unità</div>
      {missingCf > 0 && (
        <span data-testid="missing-cf-count" className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 border border-amber-300" title="Completa i codici fiscali prima della sincronizzazione catastale">
          <AlertTriangle className="h-3.5 w-3.5" />{missingCf} senza C.F.
        </span>
      )}
      <button data-testid="sort-unita" onClick={() => { setSortKey("unita"); setSortDir("asc"); }}
        className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${sortKey === "unita" ? "border-[#007BFF] text-[#007BFF] bg-[#007BFF]/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
        Raggruppa per unità
      </button>
      <Button size="sm" className="bg-[#007BFF] hover:bg-[#0056B3] h-9 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-resident-btn"
        disabled={selected.subscription_status !== "attivo"}
        title={selected.subscription_status !== "attivo" ? "Condominio non collegato: attivalo per aggiungere condòmini" : ""}
        onClick={onAddResident}>
        <Plus className="h-4 w-4 mr-1" />Aggiungi condòmino
      </Button>
      {residentsCount > 0 && (
        <Button size="sm" variant="outline" className="h-9 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="clear-anagrafica-btn"
          title="Cancella tutti i condòmini di questo condominio"
          onClick={onClearAnagrafica}>
          <Trash2 className="h-4 w-4 mr-1" />Cancella anagrafica
        </Button>
      )}
    </div>
  </div>
);
